import globalCacheManager from './globalCacheManager';

const CACHE_KEY = 'uom_conversion';

class UomConversionCache {
  constructor() {
    this.data = null;
    this.pendingPromise = null;
    this.lastFetchTime = null;
    this.isBulkUpdated = false; // Bundle로 업데이트되었는지 여부
  }

  /**
   * UOM 변환 목록 가져오기
   * @param {boolean} forceRefresh - 강제 새로고침 여부
   * @returns {Promise<Array>} UOM 변환 목록
   */
  async fetchData(forceRefresh = false) {
    // 캐시가 있고 강제 새로고침이 아니면 캐시 반환
    if (!forceRefresh && this.data) {
      return this.getData();
    }

    // 페이지 새로고침 중이면 Bundle 분배 대기 — 개별 호출 차단
    if (globalCacheManager.isRefreshing) {
      return this.data || [];
    }

    // 이미 진행 중인 요청이 있으면 그것을 반환
    if (this.pendingPromise && !forceRefresh) {
      return this.pendingPromise;
    }

    // 새로운 요청 시작
    this.pendingPromise = (async () => {
      try {
        await globalCacheManager.refreshFromBundle(forceRefresh);
        return this.data || [];
      } catch (error) {
        console.error('[UomConversionCache] Error refreshing from bundle:', error);
        return this.data || [];
      } finally {
        this.pendingPromise = null;
      }
    })();

    return this.pendingPromise;
  }

  /**
   * 캐시된 UOM 변환 목록 가져오기 (동기)
   * @returns {Array|null} UOM 변환 목록 또는 null
   */
  getData() {
    return this.data;
  }

  /**
   * 데이터 직접 설정 (Bundle 분배)
   * @param {Array} data - UOM 변환 목록
   */
  setData(data) {
    this.data = data || [];
    this.lastFetchTime = Date.now();
    this.isBulkUpdated = true; // Bundle로 업데이트됨
  }

  /**
   * 캐시 무효화
   */
  invalidate() {
    this.data = null;
    this.pendingPromise = null;
    this.lastFetchTime = null;
    this.isBulkUpdated = false;
  }

  /**
   * 마지막 갱신 시간 가져오기
   * @returns {number|null} 마지막 갱신 시간 (타임스탬프)
   */
  getLastFetchTime() {
    return this.lastFetchTime;
  }
}

// 싱글톤 인스턴스
const uomConversionCache = new UomConversionCache();

// 전역 캐시 매니저에 등록
globalCacheManager.register(CACHE_KEY, uomConversionCache);

/**
 * UOM 변환 목록 가져오기 (캐시 사용)
 * @param {boolean} forceRefresh - 강제 새로고침 여부
 * @returns {Promise<Array>} UOM 변환 목록
 */
export const fetchUomConversionListCached = async (forceRefresh = false) => {
  return await uomConversionCache.fetchData(forceRefresh);
};

/**
 * 캐시된 UOM 변환 목록 가져오기 (동기)
 * @returns {Array|null} UOM 변환 목록 또는 null
 */
export const getUomConversionListCached = () => {
  return uomConversionCache.getData();
};

/**
 * UOM 변환 캐시 무효화
 */
export const invalidateUomConversionCache = () => {
  uomConversionCache.invalidate();
};

export default uomConversionCache;
